import type {PropertyDefinitionBase, PropertySourceDetails, PropertyValue} from '@github-ui/custom-properties-types'
import {isEmptyPropertyValue} from '@github-ui/custom-properties-types/helpers'
import {enterprisePath, ownerPath} from '@github-ui/paths'
import {type Icon, KebabHorizontalIcon, PlugIcon, ShieldLockIcon, SyncIcon, UndoIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, FormControl, IconButton, Link, Stack, Token, Truncate} from '@primer/react'
import {clsx} from 'clsx'
import type {PropsWithChildren, ReactNode, RefCallback} from 'react'
import {useId, useState} from 'react'

import {getReadOnlyValue} from '../../helpers/values-table'
import type {PropertyField} from '../../hooks/use-edit-custom-properties'
import {CustomPropertyInput} from '../CustomPropertyInput'
import {CopilotSuggestValueButton} from './CopilotSuggestValueButton'
import styles from './PropertiesValuesTableRows.module.css'

interface EditValueRowProps<T extends PropertyDefinitionBase> {
  propertyField: PropertyField | undefined
  definition: T
  showUndo?: boolean
  fetchAllowedValuesUrl?: (propertyName: string) => string
  onChange: (name: string, value: PropertyValue) => void
  onReset: (name: string) => void
  required: boolean
  setRef?: RefCallback<HTMLElement>
  suggestValuePath?: string
  onCopilotSuggestion?: (propertyName: string, value?: PropertyValue) => void
}

export function EditValueRow<T extends PropertyDefinitionBase>(props: EditValueRowProps<T>) {
  const {
    propertyField,
    definition,
    showUndo,
    fetchAllowedValuesUrl,
    setRef,
    required,
    suggestValuePath,
    onCopilotSuggestion,
  } = props
  const {value, changed = false, mixed = false, error} = propertyField || {}
  const {propertyName, description} = definition

  const [copilotSuggestionExplanation, setCopilotSuggestionExplanation] = useState<string | null>(null)

  const descriptionId = useId()
  const propertyNameValidationId = `${propertyName}-validation-error`
  const copilotSuggestionExplanationId = `${propertyName}-copilot-suggestion-explanation`

  const inputDescribedByIds = clsx({
    [descriptionId]: description,
    [propertyNameValidationId]: error,
    [copilotSuggestionExplanationId]: copilotSuggestionExplanation,
  })

  // This is not strictly necessary, but it simplifies consuming it
  const onChange = (newValue: PropertyValue) => props.onChange(propertyName, newValue)
  const onReset = () => props.onReset(propertyName)

  return (
    <PropertyValueRowContainer className={styles.tableRow}>
      <DataTableRow
        label={propertyName}
        description={description}
        descriptionId={descriptionId}
        required={required}
        value={
          <>
            <div className={styles.controlsContainer}>
              <CustomPropertyInput
                setRef={setRef}
                {...definition}
                propertyValue={value}
                mixed={mixed}
                onChange={onChange}
                inputProps={inputDescribedByIds ? {'aria-describedby': inputDescribedByIds} : {}}
                fetchAllowedValuesUrl={fetchAllowedValuesUrl}
              />
            </div>
            {error && (
              <FormControl.Validation
                variant="error"
                id={propertyNameValidationId}
                className={styles.FormControl_Validation}
              >
                {error}
              </FormControl.Validation>
            )}
            {copilotSuggestionExplanation && (
              <p id={copilotSuggestionExplanationId} className="text-small color-fg-muted pt-1" aria-live="polite">
                {copilotSuggestionExplanation}
              </p>
            )}
          </>
        }
        trailing={
          <>
            {suggestValuePath ? (
              <CopilotSuggestValueButton
                propertyName={definition.propertyName}
                suggestValuePath={suggestValuePath}
                disabled={!!copilotSuggestionExplanation}
                onSuggestStart={() => setCopilotSuggestionExplanation('Generating suggestion...')}
                onCopilotSuggestion={(suggestedValue, explanation) => {
                  if (suggestedValue !== undefined) {
                    onChange(suggestedValue)
                  }
                  setCopilotSuggestionExplanation(explanation)
                  onCopilotSuggestion?.(definition.propertyName, suggestedValue)
                }}
              />
            ) : null}
            <ResetDropdown definition={definition} {...{changed, onReset, onChange, showUndo}} />
          </>
        }
      />
    </PropertyValueRowContainer>
  )
}

EditValueRow.displayName = 'EditValueRow'

interface FormReadRowProps<T extends PropertyDefinitionBase> {
  definition: T
  propertyField: PropertyField | undefined
  required: boolean
}

export function FormReadValueRow<T extends PropertyDefinitionBase>({
  propertyField,
  definition,
  required,
}: FormReadRowProps<T>) {
  const {description, propertyName} = definition
  const readOnlyValue = getReadOnlyValue(definition, propertyField?.value)

  return (
    <PropertyValueRowContainer className={styles.tableRow}>
      <DataTableRow
        readOnly
        required={required}
        label={propertyName}
        description={description}
        value={
          <Stack direction="horizontal" gap="condensed">
            <Stack.Item shrink={false}>
              <ShieldLockIcon size="small" />
            </Stack.Item>
            <Stack.Item>
              <span>{readOnlyValue}</span>
            </Stack.Item>
          </Stack>
        }
      />
    </PropertyValueRowContainer>
  )
}

interface ManagedByConfig {
  Icon: Icon
  url: string
  displayName: string
}

function getManagedByConfig(
  definition: PropertyDefinitionBase,
  managedBySource?: Omit<PropertySourceDetails, 'avatarUrl'>,
): ManagedByConfig | undefined {
  // External definitions are managed by their owner, which in this case is the installation used to set the properties
  // rather than their org or enterprise source. Therefore `managedBySource` is not relevant to external definitions.
  if (definition.type === 'external') {
    const owner = definition.owner
    if (!owner) return

    return {
      ...owner,
      Icon: PlugIcon,
    }
  }

  // If `managedBySource` is not provided the label should be omitted and therefore we return early.
  if (!managedBySource) return

  const {type, slug, name} = managedBySource
  return {
    displayName: name,
    url: type === 'org' ? ownerPath({owner: slug}) : enterprisePath({slug}),
    Icon: ShieldLockIcon,
  }
}

export function ManagedByLabel({
  definition,
  managedBySource,
}: {
  definition: PropertyDefinitionBase
  managedBySource?: Omit<PropertySourceDetails, 'avatarUrl'>
}) {
  const config = getManagedByConfig(definition, managedBySource)
  if (!config) return null

  const {Icon, url, displayName} = config
  return (
    <div className="d-inline">
      <span className={styles.mutedText}>
        <Icon size="small" /> Managed by{' '}
      </span>
      <Link href={url} inline>
        {displayName}
      </Link>
    </div>
  )
}

interface ReadRowProps<T extends PropertyDefinitionBase> {
  definition: T
  propertyField: PropertyField | undefined
  required: boolean
  managedByLabel?: ReactNode
}

export function ReadValueRow<T extends PropertyDefinitionBase>({
  propertyField,
  definition,
  required,
  managedByLabel,
}: ReadRowProps<T>) {
  const {propertyName, description} = definition

  return (
    <PropertyValueRowContainer className={styles.tableRow}>
      <DataTableRow
        readOnly
        required={required}
        label={propertyName}
        description={description}
        value={
          <DisplayPropertyValue
            propertyValue={propertyField?.value || definition.defaultValue || ''}
            mixed={propertyField?.mixed ?? false}
            definition={definition}
          />
        }
        bottomMessage={managedByLabel}
      />
    </PropertyValueRowContainer>
  )
}

function DisplayPropertyValue({
  propertyValue,
  definition,
  mixed,
}: {
  propertyValue?: PropertyValue
  definition: PropertyDefinitionBase
  mixed: boolean
}) {
  return (
    <Stack direction="horizontal" align="center" gap="condensed">
      {definition.type === 'external' && (
        <Stack.Item shrink={false}>
          <PlugIcon size="small" aria-label="External custom property value" />
        </Stack.Item>
      )}
      <Stack.Item>{getPropertyValueContent(propertyValue, definition, mixed)}</Stack.Item>
    </Stack>
  )
}

function getPropertyValueContent(
  propertyValue: PropertyValue | undefined,
  definition: PropertyDefinitionBase,
  mixed: boolean,
): ReactNode {
  const emptyStateLabel = mixed ? '(Mixed)' : isEmptyPropertyValue(propertyValue) ? '(Empty)' : undefined
  if (emptyStateLabel) {
    return <span className={styles.mutedText}>{emptyStateLabel}</span>
  }

  switch (definition.valueType) {
    case 'single_select':
    case 'true_false':
      return (
        <Token
          text={
            <Truncate title={propertyValue as string} maxWidth="100%">
              {propertyValue}
            </Truncate>
          }
          className={styles.propertyToken}
        />
      )
    case 'multi_select':
      return (
        <>
          {(propertyValue as string[]).map(v => (
            <Token
              key={v}
              text={
                <Truncate title={v} maxWidth="100%">
                  {v}
                </Truncate>
              }
              className={styles.spacedPropertyToken}
            />
          ))}
        </>
      )
    case 'string':
    case 'url':
      return (
        <Truncate title={propertyValue as string} maxWidth="100%">
          {propertyValue}
        </Truncate>
      )
  }
}

function ResetDropdown({
  definition,
  changed,
  showUndo = true,
  onReset,
  onChange,
}: {
  definition: PropertyDefinitionBase
  changed: boolean
  showUndo?: boolean
  onReset(): void
  onChange: (value: string) => void
}) {
  const {propertyName, required, source} = definition
  const undoTitle = `Undo ${propertyName} changes`
  const resetToDefaultTitle = `Reset ${propertyName} to default value`

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton
          icon={KebabHorizontalIcon}
          variant="invisible"
          aria-label={`Open reset options for ${propertyName}`}
          description="Reset options"
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="small">
        <ActionList>
          {showUndo && (
            <ActionList.Item
              onSelect={onReset}
              aria-label={undoTitle}
              inactiveText={changed ? undefined : 'No change to undo'}
            >
              <ActionList.LeadingVisual>
                <UndoIcon />
              </ActionList.LeadingVisual>
              <span className={styles.propertyLabel}>Undo</span>
              <span className={styles.propertyDescription}>Reset to the last used value</span>
            </ActionList.Item>
          )}
          {required && (
            <ActionList.Item onSelect={() => onChange('')} aria-label={resetToDefaultTitle}>
              <ActionList.LeadingVisual>
                <SyncIcon />
              </ActionList.LeadingVisual>
              <span className={styles.propertyLabel}>Reset to default</span>
              <span className={styles.propertyDescription}>
                {/* eslint-disable-next-line react-google-translate/no-conditional-text-nodes-with-siblings */}
                Inherit the value defined by the {source.type === 'business' ? 'enterprise' : 'organization'}
              </span>
            </ActionList.Item>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

export function PropertyValueRowContainer(props: PropsWithChildren<{className?: string}>) {
  return (
    <div role="listitem" data-testid="property-row" className={clsx(styles.propertyRow, props.className)}>
      {props.children}
    </div>
  )
}

function DataTableRow({
  label,
  description,
  descriptionId,
  bottomMessage,
  value,
  trailing,
  required,
  readOnly = false,
}: {
  label: string
  description: string | null
  descriptionId?: string
  bottomMessage?: ReactNode
  value: ReactNode
  trailing?: ReactNode
  required: boolean
  readOnly?: boolean
}) {
  return (
    <FormControl
      required={required}
      className={clsx(styles.FormControl, bottomMessage && styles.hasBottomMessage, !description && styles.noCaption)}
    >
      {readOnly ? (
        // Read-only rows have no input, so the name must not be a <label>
        <FormControl.Label as="span" data-testid="property-name" className={styles.FormControl_Label}>
          {label}
        </FormControl.Label>
      ) : (
        <FormControl.Label data-testid="property-name" className={styles.FormControl_Label}>
          {label}
        </FormControl.Label>
      )}
      {description && (
        <span id={descriptionId} className={styles.FormControl_Caption}>
          {description}
        </span>
      )}
      <div className={styles.formInputArea}>{value}</div>
      {trailing ? <div className={styles.actionButtonsArea}>{trailing}</div> : null}
      {bottomMessage ? <div className={styles.formMessageArea}>{bottomMessage}</div> : null}
    </FormControl>
  )
}
